import { ClientEvent, KnownMembership, SyncState } from 'matrix-js-sdk/src/matrix';
import { Dispatch, SetStateAction, useEffect } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { isSpaceInviteRoom, isVideoHubScheduledMeetingInvite } from 'modules/Notifications/inviteNotificationRoutes';
import { isInstantCallRoom } from 'utils/matrixUtils';
import { hasWorkspaceMeta } from 'utils/roomHierarchyUtils';

const SYNC_STATES_WITH_ROOMS = [SyncState.Prepared, SyncState.Syncing];

const EMPTY_SET: ReadonlySet<string> = new Set();

const useInvitedSpaces = (
  setInvitedSpaces: Dispatch<SetStateAction<string[]>>,
  backendGuestMeetingRoomIds: ReadonlySet<string> = EMPTY_SET,
  backendLoaded = false
) => {
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });

  useEffect(() => {
    if (!matrixClient) return;

    const updateInvitedWorkspaces = () => {
      const myUserId = matrixClient.getUserId();
      const rooms = matrixClient.getRooms();
      if (!rooms.length || !myUserId) return;

      const invitedWorkspaceRooms = rooms.filter((room) => {
        const membership = room.getMyMembership();
        if (membership !== KnownMembership.Invite) return false;
        if (!isSpaceInviteRoom(room)) return false;
        if (isInstantCallRoom(room)) return false;

        // Layer 1: Matrix state classification (when invite_state is rich enough).
        if (isVideoHubScheduledMeetingInvite(room)) return false;
        // Layer 2: backend guest list (catches stripped meeting invites).
        if (backendGuestMeetingRoomIds.has(room.roomId)) return false;

        // Fast path: definite workspace (has SPACE_META state event).
        if (hasWorkspaceMeta(room)) return true;

        // Stripped invite — cannot distinguish workspace from meeting by Matrix state.
        // Wait until backend confirms the room is NOT a guest meeting before including.
        return backendLoaded;
      });

      const newInvitedWorkspaces = invitedWorkspaceRooms.map((room) => room.roomId);

      // NOTE: avoid updating the state if invited workspaces do not change
      setInvitedSpaces((currentWorkspaces) => {
        // Mid-sync before backend confirms: filtered list can be transiently empty — retain.
        // After backendLoaded, allow removals (e.g. meeting space wrongly added during race).
        if (!backendLoaded && newInvitedWorkspaces.length === 0 && rooms.length > 0 && currentWorkspaces.length > 0) {
          return currentWorkspaces;
        }
        const isSameLength = newInvitedWorkspaces.length === currentWorkspaces.length;
        const currentWorkspacesSet = new Set(currentWorkspaces);
        const hasDifferentWorkspaces = newInvitedWorkspaces.some((id) => !currentWorkspacesSet.has(id));
        if (!isSameLength || hasDifferentWorkspaces) return newInvitedWorkspaces;
        return currentWorkspaces;
      });
    };

    const onSync = (state: SyncState) => {
      if (SYNC_STATES_WITH_ROOMS.includes(state)) updateInvitedWorkspaces();
    };

    // Populate immediately if the client is already in a synced state (e.g. on page reload
    // where the effect mounts after the initial sync has already completed).
    const currentSyncState = matrixClient.getSyncState();
    if (currentSyncState !== null && SYNC_STATES_WITH_ROOMS.includes(currentSyncState)) {
      updateInvitedWorkspaces();
    }

    matrixClient.on(ClientEvent.Sync, onSync);
    matrixClient.on(ClientEvent.Room, updateInvitedWorkspaces);

    return () => {
      matrixClient.off(ClientEvent.Sync, onSync);
      matrixClient.off(ClientEvent.Room, updateInvitedWorkspaces);
    };
  }, [matrixClient, setInvitedSpaces, backendGuestMeetingRoomIds, backendLoaded]);
};

export default useInvitedSpaces;
